'use client'

import { useMemo, useSyncExternalStore } from 'react'
import { LanguageRecord } from '@/graphql/types'
import useSWR from 'swr'
import { usePathname } from 'next/navigation'
import { transformLocale } from '@/utils/language'
import { ArrowRight, XIcon } from 'lucide-react'
import { Button } from '@desoutter/uikit'
import { useLocaleBanner } from '@/hooks/useLocaleBanner'

type HeaderBannerLocaleProps = {
	label: string
}

const fetcher = async (url: string) => {
	const responseCountry = await fetch(
		'https://geolocation.onetrust.com/cookieconsentpub/v1/geo/location',
		{
			headers: {
				Accept: 'application/json',
			},
		},
	)
	const dataCountry = await responseCountry.json()

	const res = await fetch(`${url}?country=${dataCountry.country}`)
	if (!res.ok) throw new Error('Failed to fetch locale data')
	return res.json()
}

export default function HeaderBannerLocale({ label }: HeaderBannerLocaleProps) {
	const pathname = usePathname()
	const showBanner = useLocaleBanner()

	const { data, isLoading, error } = useSWR<
		Pick<LanguageRecord, 'countryCode' | 'value' | 'label'>
	>(showBanner ? '/api/locales/header/redirection' : null, fetcher, {
		errorRetryCount: 0,
		revalidateOnFocus: false,
		revalidateIfStale: false,
		revalidateOnReconnect: false,
	})

	const redirection = useMemo(() => {
		if (!data) return null
		const currentLocale = pathname.split('/')[1]

		if (
			currentLocale ===
			transformLocale({
				locale: data.value,
				to: 'params',
			})
		) {
			return null
		}

		return data
	}, [data, pathname])

	const handleDismiss = () => {
		localStorage.setItem('locale_banner_seen', 'true')
		window.dispatchEvent(new Event('storage'))
	}

	if (!showBanner || isLoading || !redirection || error) return null

	const redirectUrl = new URL(
		pathname.split('/').slice(2).join('/'),
		`https://${process.env.NEXT_PUBLIC_HOST}`,
	)
	redirectUrl.pathname = `/${transformLocale({
		locale: redirection.value,
		to: 'params',
	})}${redirectUrl.pathname}`

	return (
		<div
			id="locale-banner"
			className="text-sm bg-background text-foreground w-full px-4 flex items-center justify-between shadow-sm border-b border-foreground/10 py-4"
			role="alert"
		>
			<div className="container mx-auto flex justify-between gap-4">
				<div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
					<span>{label ? label : 'Redirect to the appropriate locale'}</span>
					<a
						className="inline-flex items-center gap-2 p-2 shadow-inner bg-foreground/20 rounded-md"
						href={redirectUrl.toString()}
						title={redirection.label}
						hrefLang={redirection.countryCode?.toLowerCase()}
					>
						{redirection.label}
						<ArrowRight className="w-4 h-4" />
					</a>
				</div>
				<Button
					onClick={handleDismiss}
					className="bg-background text-foreground border-foreground stroke-foreground hover:bg-background/80 flex-shrink-0"
					variant="outline"
					size="icon"
					noIcon
					aria-label="Dismiss language suggestion"
				>
					<XIcon className="w-4 h-4" />
				</Button>
			</div>
		</div>
	)
}
